import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminAPI from '../services/adminAPI';
import { companiesAPI } from '../services/api';
import useStore from '../store/useStore';
import { useCrudOperations } from '../hooks/useCrudOperations';
import '../styles/UserManagement.css';

const UserManagement = () => {
  const navigate = useNavigate();
  const currentUser = useStore((state) => state.user);
  const userCompanies = useStore((state) => state.companies);

  const {
    data: users,
    loading,
    error,
    successMessage,
    loadData: loadUsers,
    create: createUser,
    update: updateUser,
    remove: removeUser,
    clearError
  } = useCrudOperations({
    apiService: adminAPI.users || adminAPI,
    resourceName: 'user'
  });

  const [allCompanies, setAllCompanies] = useState([]);
  const [allDashboards, setAllDashboards] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editData, setEditData] = useState({});

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    role: 'user',
    companyIds: [],
  });

  const isSuperAdmin = currentUser?.role === 'super_admin';

  useEffect(() => {
    loadUsers();
    loadDashboards();
    // Super admins need to load all companies for assignment
    if (isSuperAdmin) {
      loadAllCompanies();
    }
  }, [isSuperAdmin]);

  const loadDashboards = async () => {
    try {
      const { dashboardsAPI } = await import('../services/api');
      const response = await dashboardsAPI.getAll();
      if (response.success) {
        setAllDashboards(response.data);
      }
    } catch (error) {
      console.error('Error loading dashboards:', error);
    }
  };

  const loadAllCompanies = async () => {
    try {
      const response = await companiesAPI.getAll();
      if (response.success) {
        setAllCompanies(response.data);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCompanyToggle = (companyId) => {
    setFormData((prev) => {
      const companyIds = prev.companyIds.includes(companyId)
        ? prev.companyIds.filter((id) => id !== companyId)
        : [...prev.companyIds, companyId];
      return { ...prev, companyIds };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    // Validate company selection (not required for super admins as they have access to all companies)
    if (formData.role !== 'super_admin' && (!formData.companyIds || formData.companyIds.length === 0)) {
      // Manual validation - need to handle this without hook
      alert('Please select at least one company');
      return;
    }

    // Call API directly due to complex signature
    try {
      const response = await adminAPI.createUser(
        formData.username,
        formData.email,
        formData.password,
        formData.fullName,
        formData.role,
        formData.role === 'super_admin' ? [] : formData.companyIds
      );

      if (response.success) {
        setFormData({ username: '', email: '', password: '', fullName: '', role: 'user', companyIds: [] });
        setShowUserForm(false);
        await loadUsers();
      }
    } catch (error) {
      console.error('Error saving user:', error);
      // Error handled by hook or shown to user
    }
  };

  const handleEditUser = async (user) => {
    setEditingUserId(user.id);
    setEditData({
      email: user.email,
      fullName: user.full_name || '',
      role: user.role,
      password: '',
      companyIds: [],
      dashboardIds: [],
    });

    // Load user's companies if not super admin
    if (user.role !== 'super_admin') {
      try {
        const response = await adminAPI.getUserCompanies(user.id);
        if (response.success) {
          const companyIds = response.data.map(c => c.id);
          setEditData(prev => ({ ...prev, companyIds }));
        }
      } catch (error) {
        console.error('Error loading user companies:', error);
      }
    }

    // Load user's dashboards if regular user
    if (user.role === 'user') {
      try {
        const response = await adminAPI.getUserDashboards(user.id);
        if (response.success) {
          const dashboardIds = response.data.map(d => d.id);
          setEditData(prev => ({ ...prev, dashboardIds }));
        }
      } catch (error) {
        console.error('Error loading user dashboards:', error);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditData({});
    clearError();
  };

  const handleEditCompanyToggle = (companyId) => {
    setEditData((prev) => {
      const companyIds = prev.companyIds.includes(companyId)
        ? prev.companyIds.filter((id) => id !== companyId)
        : [...prev.companyIds, companyId];
      return { ...prev, companyIds };
    });
  };

  const handleEditDashboardToggle = (dashboardId) => {
    setEditData((prev) => {
      const dashboardIds = prev.dashboardIds.includes(dashboardId)
        ? prev.dashboardIds.filter((id) => id !== dashboardId)
        : [...prev.dashboardIds, dashboardId];
      return { ...prev, dashboardIds };
    });
  };

  const handleSaveEdit = async (userId) => {
    clearError();

    // Validate company selection for non-super-admin users
    if (editData.role !== 'super_admin' && (!editData.companyIds || editData.companyIds.length === 0)) {
      alert('Please select at least one company');
      return;
    }

    try {
      // Update user details
      const response = await adminAPI.updateUser(userId, {
        email: editData.email,
        fullName: editData.fullName,
        role: editData.role,
        password: editData.password
      });

      if (response.success) {
        // Update company assignments for non-super-admin users
        if (editData.role !== 'super_admin') {
          await adminAPI.updateUserCompanies(userId, editData.companyIds);
        }

        // Update dashboard assignments for regular users
        if (editData.role === 'user') {
          await adminAPI.updateUserDashboards(userId, editData.dashboardIds);
        }

        setEditingUserId(null);
        setEditData({});
        await loadUsers();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      // Error will be shown via hook's error state
    }
  };

  const handleDeleteUser = async (userId, username) => {
    await removeUser(
      userId,
      `Are you sure you want to delete user "${username}"? This action cannot be undone.`,
      'User deleted successfully'
    );
  };

  const handleNewUser = () => {
    setFormData({ username: '', email: '', password: '', fullName: '', role: 'user', companyIds: [] });
    setShowUserForm(true);
  };

  const handleCancel = () => {
    setShowUserForm(false);
    setFormData({ username: '', email: '', password: '', fullName: '', role: 'user', companyIds: [] });
  };

  if (loading) {
    return (
      <div className="user-management-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <h2>User Management</h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button onClick={handleNewUser} className="btn-primary">
            + Create New User
          </button>
          <button onClick={() => navigate('/settings')} className="btn-secondary">
            Back to Settings
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {showUserForm && (
        <div className="user-form-section">
          <h3>Create New User</h3>
          <form onSubmit={handleSubmit} className="user-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      minLength={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={8}
                    />
                  </div>
                </div>

            <div className="form-group">
              <label>Role *</label>
              <select name="role" value={formData.role} onChange={handleChange} required>
                <option value="user">User - Can view dashboards</option>
                <option value="admin">Admin - Can manage users and resources for assigned companies</option>
                {currentUser?.role === 'super_admin' && (
                  <option value="super_admin">Super Admin - Full access to all companies</option>
                )}
              </select>
            </div>

            {formData.role !== 'super_admin' && (
              <div className="form-group">
                <label>Assign to Companies * (Select at least one)</label>
                <div className="companies-checkbox-grid">
                  {(isSuperAdmin ? allCompanies : userCompanies) && (isSuperAdmin ? allCompanies : userCompanies).length > 0 ? (
                    (isSuperAdmin ? allCompanies : userCompanies).map((company) => (
                      <label key={company.id} className="company-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.companyIds.includes(company.id)}
                          onChange={() => handleCompanyToggle(company.id)}
                        />
                        <span>{company.name}</span>
                      </label>
                    ))
                  ) : (
                    <p>Loading companies...</p>
                  )}
                </div>
              </div>
            )}
            {formData.role === 'super_admin' && (
              <div className="form-group">
                <div style={{ padding: '1rem', backgroundColor: 'rgba(74, 158, 255, 0.1)', borderRadius: '6px', border: '1px solid var(--accent-blue)' }}>
                  <strong style={{ color: 'var(--accent-blue)' }}>ℹ️ Super Admin Access</strong>
                  <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    Super admins automatically have access to all companies. No company assignments are needed.
                  </p>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Create User
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-section">
        <h3>Existing Users ({users.length})</h3>
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Full Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users
                .filter(user => isSuperAdmin || user.role !== 'super_admin')
                .map((user) => {
                  const isEditing = editingUserId === user.id;

                  return (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>
                        {isEditing ? (
                          <input
                            type="email"
                            value={editData.email}
                            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                            style={{ width: '100%', padding: '0.4rem', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-primary)' }}
                          />
                        ) : (
                          user.email
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.fullName}
                            onChange={(e) => setEditData({ ...editData, fullName: e.target.value })}
                            placeholder="Full Name"
                            style={{ width: '100%', padding: '0.4rem', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-primary)' }}
                          />
                        ) : (
                          user.full_name || '-'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <div>
                            <select
                              value={editData.role}
                              onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                              disabled={user.id === currentUser.id}
                              style={{ width: '100%', padding: '0.4rem', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-primary)', opacity: user.id === currentUser.id ? 0.6 : 1, cursor: user.id === currentUser.id ? 'not-allowed' : 'pointer' }}
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                              {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                            </select>
                            {user.id === currentUser.id && (
                              <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--text-secondary)', fontSize: '0.75em' }}>
                                You cannot change your own role
                              </small>
                            )}
                            {editData.role !== 'super_admin' && (
                              <div style={{ marginTop: '0.5rem', maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '0.5rem', backgroundColor: 'var(--bg-secondary)' }}>
                                <small style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                                  Assigned Companies:
                                </small>
                                {(isSuperAdmin ? allCompanies : userCompanies) && (isSuperAdmin ? allCompanies : userCompanies).length > 0 ? (
                                  (isSuperAdmin ? allCompanies : userCompanies).map((company) => (
                                    <label key={company.id} style={{ display: 'flex', alignItems: 'center', padding: '0.25rem', cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={editData.companyIds?.includes(company.id) || false}
                                        onChange={() => handleEditCompanyToggle(company.id)}
                                        style={{ marginRight: '0.5rem' }}
                                      />
                                      <span style={{ fontSize: '0.85em' }}>{company.name}</span>
                                    </label>
                                  ))
                                ) : (
                                  <small>Loading companies...</small>
                                )}
                              </div>
                            )}
                            {editData.role === 'user' && (
                              <div style={{ marginTop: '0.5rem', maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '0.5rem', backgroundColor: 'var(--bg-secondary)' }}>
                                <small style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                                  Assigned Dashboards:
                                </small>
                                {allDashboards && allDashboards.length > 0 ? (
                                  allDashboards
                                    .filter(dashboard => editData.companyIds?.includes(dashboard.company_id))
                                    .map((dashboard) => (
                                      <label key={dashboard.id} style={{ display: 'flex', alignItems: 'center', padding: '0.25rem', cursor: 'pointer' }}>
                                        <input
                                          type="checkbox"
                                          checked={editData.dashboardIds?.includes(dashboard.id) || false}
                                          onChange={() => handleEditDashboardToggle(dashboard.id)}
                                          style={{ marginRight: '0.5rem' }}
                                        />
                                        <span style={{ fontSize: '0.85em' }}>
                                          {dashboard.name} <span style={{ color: 'var(--text-secondary)' }}>({dashboard.company_name})</span>
                                        </span>
                                      </label>
                                    ))
                                ) : (
                                  <small>Loading dashboards...</small>
                                )}
                                {allDashboards.filter(d => editData.companyIds?.includes(d.company_id)).length === 0 && (
                                  <small style={{ color: 'var(--text-secondary)' }}>Select companies first to see available dashboards</small>
                                )}
                              </div>
                            )}
                            {editData.role === 'admin' && (
                              <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--accent-blue)', fontSize: '0.85em' }}>
                                ℹ️ Full access to all dashboards in assigned companies
                              </small>
                            )}
                            {editData.role === 'super_admin' && (
                              <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--accent-blue)', fontSize: '0.85em' }}>
                                ℹ️ Full access to all companies and dashboards
                              </small>
                            )}
                          </div>
                        ) : (
                          <>
                            <span className={`role-badge ${user.role}`}>
                              {user.role === 'super_admin' ? '⭐ Super Admin' :
                               user.role === 'admin' ? '👑 Admin' : 'User'}
                            </span>
                            {user.role !== 'super_admin' && user.company_count !== undefined && (
                              <small style={{ display: 'block', color: '#888', fontSize: '0.85em' }}>
                                {user.company_count} {user.company_count === 1 ? 'company' : 'companies'}
                              </small>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="password"
                            value={editData.password}
                            onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                            placeholder="New password (optional)"
                            style={{ width: '100%', padding: '0.4rem', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-primary)' }}
                          />
                        ) : (
                          new Date(user.created_at).toLocaleDateString()
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => handleSaveEdit(user.id)}
                              className="btn-primary-small"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="btn-secondary-small"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="btn-secondary-small"
                            >
                              Edit
                            </button>
                            {user.id !== currentUser.id && (
                              (isSuperAdmin || (!isSuperAdmin && user.role === 'user')) && (
                                <button
                                  onClick={() => handleDeleteUser(user.id, user.username)}
                                  className="btn-danger-small"
                                >
                                  Delete
                                </button>
                              )
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
